import {Member} from '@src/Api/Umbraco/interface/Member.interface';
import apiConfig from '@src/Configs/apiConfig';
import {markBrazeReady} from './brazeQueue';

export const generateAnonymousUserId = () => {
  return `anon_${Math.random().toString(36).substring(2, 11)}`;
};

/**
 * Clear all local Braze data (identity, cached events, push token) on logout so
 * the signed-out user's profile and dead push tokens do not persist or pile up
 * on the device (audit #7). Safe no-op on SSR / if the SDK is not loaded.
 */
export const clearBrazeData = (): void => {
  if (typeof window === 'undefined') return;
  import('./braze-exports')
    .then(({wipeData}) => wipeData())
    .catch(() => {
      /* never let Braze cleanup block logout */
    });
};

const useBraze = () => {
  const initBraze = () => {
    if (typeof window !== 'undefined') {
      import('./braze-exports')
        .then(({initialize, isInitialized, automaticallyShowInAppMessages}) => {
          const started = initialize(apiConfig.brazeApiKey, {
            baseUrl: apiConfig.brazeBaseUrl,
            // Commented while implementing braze push notifications - Looks like domain is not whitelisted yet on Braze
            // serviceWorkerLocation: '/sw.js'
          });

          automaticallyShowInAppMessages();

          // Release anything the adapter buffered while this dynamic import was
          // in flight. The Braze SDK discards events logged before init
          // (returns false, no queue) — see brazeQueue.ts. `initialize()`
          // returns false when it was already initialised, hence the
          // isInitialized() fallback.
          if (started || isInitialized()) {
            markBrazeReady();
          }
        })
        .catch(err => {
          // Braze unavailable (blocked, offline, chunk error). Leave the queue
          // unflushed and bounded rather than pretending the SDK is ready.
          // eslint-disable-next-line no-console
          console.warn('[braze] init failed', err);
        });
    }
  };

  const openBrazeSession = (
    userId?: string,
    member?: Member,
  ): Promise<void> => {
    if (typeof window !== 'undefined') {
      return new Promise((resolve, reject) => {
        const email = member?.email;

        import('./braze-exports')
          .then(
            ({
              changeUser,
              openSession,
              isPushSupported,
              requestPushPermission,
              getUser,
              requestImmediateDataFlush,
            }) => {
              // Only re-identify Braze when we actually know the user. The old
              // code called changeUser() with a fresh random anonymous id on
              // every page load, which created a brand-new Braze profile each
              // time (the "new users" flood: 82-93% of DAU). For anonymous
              // sessions we now just openSession(), which reuses Braze's own
              // persisted anonymous id, and changeUser(userId) on login aliases
              // that persisted profile to the real member instead of orphaning it.
              if (userId) {
                changeUser(userId);
              }
              openSession();
              email && getUser()?.setEmail(email);
              requestImmediateDataFlush();
              setTimeout(() => {
                if (isPushSupported()) {
                  console.log('isPushSupported---true');
                  // If the browser has already denied notifications, don't call
                  // the Braze push flow (it will attempt subscription and can
                  // produce internal stack traces). Reject early with a clear
                  // message so callers can handle it gracefully.
                  if (
                    typeof Notification !== 'undefined' &&
                    Notification.permission === 'denied'
                  ) {
                    console.log(
                      'Browser notification permission already denied - skipping requestPushPermission',
                    );
                    reject(new Error('Browser push permission denied'));
                    return;
                  }

                  requestPushPermission(
                    (endpoint, publicKey, userAuth) => {
                      console.log('requestPushPermission---endpoint', endpoint);
                      console.log(
                        'requestPushPermission---publicKey',
                        publicKey,
                      );
                      console.log('requestPushPermission---userAuth', userAuth);
                      resolve();
                    },
                    () => {
                      console.log('requestPushPermission---denied');
                      reject(new Error('Braze push permission denied'));
                    },
                  );
                } else {
                  console.log('isPushSupported---false');
                  reject(new Error('Braze push not supported'));
                }
              }, 300);
            },
          )
          .catch(error => {
            console.error('Error initializing Braze session', error);
            // Ensure we always reject with an Error instance
            if (error instanceof Error) {
              reject(error);
            } else {
              reject(new Error(String(error)));
            }
          });
      });
    }
    return Promise.resolve();
  };
  return {
    initBraze,
    openBrazeSession,
  };
};

export default useBraze;
